import { useAuth0 } from '@auth0/auth0-react';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import { useFilters } from 'components/Dashboard/Filters/FiltersContext';
import OutsideFilters from 'components/Dashboard/Filters/ui/OutsideFilters';
import useGetSectionFilters from 'components/Dashboard/Filters/useGetSectionFilters';
import { NotesListSkeleton, NotesListItemSkeleton } from 'components/Dashboard/NotesPage/NotesPageSkeleton';
import NotesPageListItem from 'components/Dashboard/NotesPage/NotesPageListItem';
import NotesPageSideBar from 'components/Dashboard/NotesPage/NotesPageSideBar';
import NotesPageSubNav from 'components/Dashboard/NotesPage/NotesPageSubNav';
import useStakeholderNotesFunctions from 'components/Dashboard/StakeHolders/StakeholderPage/useStakeholderNotesFunctions';
import { getUrlParamRaw, decodeUrlQueryValue } from 'components/Dashboard/utilities/getUrlParam';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import { store } from 'components/Store';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import useDetectActiveFilters from 'components/Dashboard/SubNav/useDetectActiveFilters';
import useChangeClient from 'components/Dashboard/Navigation/useChangeClient';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';

const NotesPage = () => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { contentResults, setContentResults } = useContentResults();
  const { team, activePlan } = state;
  const { myself, teamId } = team ?? {};

  const [heightContainer, containerRef] = useHeightContainer();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const { newAgencyPlan } = useTopicOrClientWord();
  const { user } = useAuth0();
  const scrollBarRef = useRef(null);
  const initialCall = useRef(null);

  const params = useParams();
  const { notesAuthor } = params;
  const navigateToNotesAuthor = (notesAuthor) => {
    navigate(
      { pathname: `/notes/${notesAuthor}`, search: location.search, hash: window.location.hash },
      { replace: true }
    );
  };
  const { clearFilters, contentFilters } = useFilters();

  const { detectActiveFilters } = useDetectActiveFilters();
  // detectActiveFilters only tracks sidebar checkbox filters, not the free-text 'search' URL param,
  // so a search-only query wouldn't otherwise count as an active filter for the empty-state message.
  const hasActiveFilters = detectActiveFilters() || !!getUrlParamRaw('search');
  const { getSectionFilters } = useGetSectionFilters('Notes');
  const sectionFilters = getSectionFilters();
  const { hash, filters } = sectionFilters ?? {};
  const { getNotesList } = useStakeholderNotesFunctions();
  const { changeClientFunction } = useChangeClient();

  const [pageNumber, setPageNumber] = useState(1);
  const [isLoadingScroll, setIsLoadingScroll] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const pageSize = parseInt(import.meta.env.VITE_APP_PAGE_SIZE);

  const notes = contentResults?.NotesPage ?? [];
  const setNotes = (value) => {
    setContentResults({
      ...contentResults,
      NotesPage: value,
    });
  };
  const searchQuery = getUrlParamRaw('search') ? decodeUrlQueryValue(getUrlParamRaw('search')) : '';
  const createNotesListResults = async ({ source, pageNumber }) => {
    const userId =
      !notesAuthor || notesAuthor === 'my-teams'
        ? undefined
        : notesAuthor === 'my-own'
          ? myself?.id
          : !isNaN(notesAuthor) && !isNaN(parseInt(notesAuthor))
            ? parseInt(notesAuthor)
            : undefined;
    const contentTypes = filters?.filter((filter) => filter.field === 'contentType');
    // The Association filter is a checkbox group (Policymakers/Interactions/Updates), not mutually
    // exclusive - checking more than one of these bucket-covering categories together is equivalent
    // to no filter at all, same as checking none.
    const contentType = contentTypes.length === 1 ? (contentTypes[0]?.value ?? undefined) : undefined;
    const noteVisibility = filters?.find((filter) => filter.field === 'noteVisibility')?.value ?? undefined;
    const dateTimeFromUtc = filters?.find(
      (filter) => filter.field === 'dateTime' && filter.operator === 'DATE_GTE'
    )?.value;
    const dateTimeToUtc = filters?.find(
      (filter) => filter.field === 'dateTime' && filter.operator === 'DATE_LT'
    )?.value;

    const userIds = filters.filter((item) => item.field === 'userId').map((item) => item.value);
    const params = {
      query: searchQuery,
      userIds: userIds?.length > 0 ? userIds : userId ? [userId] : undefined,
      contentType,
      dateTimeFromUtc,
      dateTimeToUtc,
      noteVisibility,
    };

    const results = await getNotesList({
      requestSource: source,
      pageNumber,
      pageSize,
      ...params,
    });
    return results;
  };

  initialCall.current = async (source) => {
    setIsLoading(true);
    const results = await createNotesListResults({ source, pageNumber: 1 });
    if (results) {
      setNotes(results);
      setIsLoading(false);
    }
    navigate({ search: window.location.search, hash: !!hash ? `#${hash}` : '' }, { replace: true });
  };

  useEffect(() => {
    const source = new AbortController();
    initialCall.current(source);
    return () => {
      source.abort('Notes call cancelled by the user');
      setNotes([]);
    };
  }, [contentFilters, notesAuthor, searchQuery, teamId]);

  const applyClientFilter = async (clientId) => {
    if (newAgencyPlan && team?.teamId !== clientId) {
      await changeClientFunction({ clientId: clientId });
    }
  };

  useEffect(() => {
    const clientIds = filters.filter((item) => item.field === 'client').map((item) => item.value);
    if (clientIds.length > 0 && parseFloat(clientIds[0]) !== team?.teamId) {
      applyClientFilter(parseFloat(clientIds[0]));
    }
  }, [contentFilters]);

  const thereIsMoreContent = notes?.length !== 0 && notes?.length % pageSize === 0;

  const handleScroll = (scrollValues) => {
    const { scrollTop, scrollHeight, clientHeight } = scrollValues;
    const threshold = 1;
    const scrolledToBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight - threshold;
    if (scrolledToBottom && thereIsMoreContent) {
      load_more_results();
    }
  };

  const load_more_results = async () => {
    if (!isLoadingScroll) {
      setIsLoadingScroll(true);
      let newPage = pageNumber + 1;
      let parameter = newPage;
      const results = await createNotesListResults({ pageNumber: parameter });
      if (results) {
        const newNotes = [...notes, ...results];
        setNotes(newNotes);
        setPageNumber(parameter);
        setIsLoadingScroll(false);
      }
    }
  };

  return (
    <>
      <NotesPageSubNav notesAuthor={notesAuthor} setNotesAuthor={navigateToNotesAuthor} />
      <div className='row mx-auto main-content-wrapper py-3 pt-lg-5 pb-lg-0 flex-grow-1 '>
        <div className={`side-left-element d-none d-lg-block`}>
          <div className='pl-lg-4 pr-4 pr-lg-5'>
            <NotesPageSideBar notesAuthor={notesAuthor} setNotesAuthor={navigateToNotesAuthor} />
          </div>
        </div>

        <div className={`main-content pr-xl-5 px-md-4 px-3`}>
          <div className='row mx-0'>
            <div className={`px-0 flex-grow-1 mw-100 reference-main-content`} style={{ flex: 1 }}>
              <div ref={containerRef} />
              {isLoading ? (
                <NotesListSkeleton />
              ) : (
                <>
                  {notes.length === 0 && (
                    <div className='box-top-rounded pt-5' ref={containerRef} style={{ height: `${heightContainer}px` }}>
                      <div className='pt-5 text-center'>
                        <p className=' mt-5 pt-5 title-h4 main-light-text px-5 d-flex flex-wrap justify-content-center'>
                          {hasActiveFilters ? (
                            <span className='nowrap-item'>No notes found</span>
                          ) : notesAuthor === 'my-own' ? (
                            <>
                              <span className='nowrap-item'>Notes you write about policymakers</span>&nbsp;
                              <span className='nowrap-item'>or updates will appear here</span>
                            </>
                          ) : (
                            <>
                              <span className='nowrap-item'>Notes you or your team write about </span>&nbsp;
                              <span className='nowrap-item'>policymakers or updates will appear here</span>
                            </>
                          )}
                        </p>
                        {hasActiveFilters && (
                          <button
                            className={`general-button action-button empty-button third-button`}
                            onClick={() => {
                              clearFilters();
                              if (searchQuery) {
                                const query = new URLSearchParams(location.search);
                                query.delete('search');
                                navigate({ pathname: location.pathname, search: query.toString() }, { replace: true });
                              }
                            }}
                          >
                            Clear filters
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  {notes.length > 0 && (
                    <CustomScrollbar
                      className={`main-content-scrollbar`}
                      style={{ height: `${heightContainer}px` }}
                      maximalThumbYSize={100}
                      ref={scrollBarRef}
                      onScroll={(scrollValues) => {
                        handleScroll(scrollValues);
                      }}
                    >
                      <ul className='policy-list px-0 pb-2 all-open-consultations'>
                        {notes?.map((note) => (
                          <NotesPageListItem
                            key={note.id}
                            item={note}
                            team={team}
                            activePlan={activePlan}
                            user={user}
                          />
                        ))}
                        {thereIsMoreContent && <NotesListItemSkeleton />}
                      </ul>
                    </CustomScrollbar>
                  )}
                </>
              )}
            </div>
            {!isMobile && (
              <div className='ml-4 outside-filters-container'>
                <OutsideFilters section={'Notes'} />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default NotesPage;
